// Tiny module-level singleton that owns the global `beforeinstallprompt` event
// and platform detection. Any component (the floating install banner, an "Install
// app" menu item, an admin debug panel, …) can call `usePwaInstall()` and stay
// in sync — the deferred prompt fires exactly once per page load but multiple
// pieces of UI may want to consume it.

import { useEffect, useState } from "react";

let deferredPrompt = null;
let installedAt = 0;
const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* swallow */ }
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installedAt = Date.now();
    notify();
  });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

export function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !window.MSStream;
}

export function isAndroid() {
  if (typeof window === "undefined") return false;
  return /android/i.test(window.navigator.userAgent);
}

// Detect in-app browsers (Instagram, Facebook, WhatsApp, etc) where the OS
// install flow is blocked entirely — we have to tell the user to open in
// system Chrome / Safari.
export function isInAppBrowser() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /Instagram|FBAN|FBAV|FB_IAB|FB4A|Line\/|MicroMessenger|WhatsApp|wv\)/i.test(ua);
}

// Best-effort Chromium-on-Android detection. We only flip to "use Chrome
// instead" advice when the user is clearly NOT already on a Chromium browser,
// so iPhone-on-Safari users don't get told to install Chrome.
export function isChromiumAndroid() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  if (!/Android/i.test(ua)) return false;
  // Chrome, Edge, Brave, Samsung Internet — all support beforeinstallprompt.
  return /Chrome\/|EdgA\/|Brave\/|SamsungBrowser\//i.test(ua);
}

export function getDeferredPrompt() {
  return deferredPrompt;
}

// React hook — re-renders when the underlying singleton changes (BIP fires,
// appinstalled fires, …). Returns flags + an `install()` function that
// transparently consumes the deferred prompt.
export function usePwaInstall() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);

  const standalone = isStandalone();
  const canPrompt = !!deferredPrompt && !standalone;

  async function install() {
    const p = deferredPrompt;
    if (!p) return { outcome: "unavailable" };
    deferredPrompt = null;
    notify();
    try {
      p.prompt();
      const result = await p.userChoice;
      return result; // { outcome: 'accepted' | 'dismissed' }
    } catch {
      return { outcome: "error" };
    }
  }

  return {
    standalone,
    canPrompt,
    ios: isIOS(),
    android: isAndroid(),
    chromiumAndroid: isChromiumAndroid(),
    inAppBrowser: isInAppBrowser(),
    installedAt,
    install,
  };
}
